import type { AggregatesArtifact, DashboardHandoff, EvidenceClaim } from '../../data/types';
import { useBeatOrchestration } from '../../motion/useBeatOrchestration';

function sourceLabel(source: EvidenceClaim['source']) {
  if (source === 'coded-response') return '選択式回答';
  if (source === 'reviewed-voice-theme') return '自由記述の分類';
  if (source === 'brand-document') return 'ブランド資料';
  return '編集部による統合';
}

function EvidenceCard({ claim }: { claim: EvidenceClaim }) {
  return <div data-reveal-item className="rounded-3xl border border-atc-navy/10 bg-white/90 p-5 shadow-atc-card">
    <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold">
      <span className="rounded-full bg-atc-primary/8 px-3 py-1 text-atc-primary">{sourceLabel(claim.source)}</span>
      {claim.small && <span className="rounded-full bg-orange-50 px-3 py-1 text-atc-ink">参考値</span>}
    </div>
    <p className="mt-5 text-sm font-bold text-atc-slate">{claim.label}</p>
    <strong className="mt-2 block text-4xl leading-tight tracking-[-.04em] text-atc-navy md:text-5xl">{claim.value}</strong>
    <p className="mt-3 text-xs leading-relaxed text-atc-slate">
      {claim.numerator !== null && claim.denominator !== null
        ? `n=${claim.numerator.toLocaleString()} / ${claim.denominator.toLocaleString()}`
        : claim.numerator !== null ? `確認できた件数 n=${claim.numerator.toLocaleString()}` : '数値ではなく定義・統合'}
    </p>
    {claim.caveat && <p className="mt-3 border-t border-atc-navy/10 pt-3 text-xs leading-relaxed text-atc-slate">注記：{claim.caveat}</p>}
  </div>;
}

function BrandTension({ story }: { story: AggregatesArtifact['story'] }) {
  const frictions = story.negativeThemes.filter((theme) => ['empty', 'aging', 'unclear', 'low-vitality'].includes(theme.id));
  return <div className="grid gap-5 md:grid-cols-2">
    <section className="rounded-3xl bg-atc-primary p-6 text-white" aria-label="余白を価値として捉えた自由記述">
      <p className="text-xs font-bold tracking-[.16em] text-white/70">余白が価値に見えた声</p>
      <div className="mt-6 space-y-4">{story.positiveThemes.map((theme) => <div data-reveal-item key={theme.id}>
        <div className="flex items-end justify-between gap-3"><span className="text-sm font-bold">{theme.label}</span><strong className="text-2xl tabular-nums">{theme.n}</strong></div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/15"><span className="block h-full rounded-full bg-white" style={{ width: `${Math.max(8, theme.n / 152 * 100)}%` }} /></div>
      </div>)}</div>
      <p className="mt-6 text-xs leading-relaxed text-white/70">ATCイメージ欄の監査済み分類（各 n / 915）。複数テーマを含む回答があります。</p>
    </section>
    <section className="rounded-3xl border border-atc-accent/25 bg-white p-6" aria-label="余白を不足として捉えた自由記述">
      <p className="text-xs font-bold tracking-[.16em] text-atc-accent">余白が空洞に見えた声</p>
      <div className="mt-6 space-y-4">{frictions.map((theme) => <div data-reveal-item key={theme.id}>
        <div className="flex items-end justify-between gap-3"><span className="text-sm font-bold text-atc-ink">{theme.label}</span><strong className="text-2xl tabular-nums text-atc-accent">{theme.n}</strong></div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-atc-navy/8"><span className="block h-full rounded-full bg-atc-accent" style={{ width: `${Math.max(8, theme.n / 47 * 100)}%` }} /></div>
      </div>)}</div>
      <p className="mt-6 text-xs leading-relaxed text-atc-slate">ATCイメージ欄の監査済み分類（各 n / 915）。Action Orange は課題側だけに使用。</p>
    </section>
  </div>;
}

function DataStorySlide({ aggregates, index, onHandoff }: {
  aggregates: AggregatesArtifact;
  index: number;
  onHandoff(tab: DashboardHandoff['tab']): void;
}) {
  const slide = aggregates.story.dataStories[index]!;
  const claims = slide.evidenceIds.map((id) => aggregates.story.evidence.find((claim) => claim.id === id)).filter((claim): claim is EvidenceClaim => Boolean(claim));
  const motionRoot = useBeatOrchestration({ active: true });
  return <article id={slide.id} className="grid min-h-[92svh] scroll-mt-20 content-center px-6 py-24 md:px-12 lg:px-20" aria-labelledby={`${slide.id}-heading`}>
    <div ref={motionRoot} className="mx-auto w-full max-w-[88rem]">
      <div data-section-transition className="mb-7 h-px w-full origin-left bg-gradient-to-r from-atc-primary via-atc-primary/35 to-transparent" aria-hidden="true" />
      <p data-beat-part data-beat-kicker className="text-xs font-bold tracking-[.2em] text-atc-primary">{slide.marker} ・ REPORT FINDING</p>
      <p data-beat-part data-beat-dots className="mt-5 max-w-2xl text-base font-bold leading-relaxed text-atc-slate">{slide.question}</p>
      <h2 data-beat-part data-beat-question id={`${slide.id}-heading`} className="mt-4 max-w-5xl text-3xl font-bold leading-tight tracking-[-.04em] text-atc-navy md:text-5xl">{slide.title}</h2>
      <div data-beat-part data-beat-visual className="mt-10">
        {slide.id === 'brand-yohaku' ? <BrandTension story={aggregates.story} /> : <div className={`grid gap-5 ${claims.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>{claims.map((claim) => <EvidenceCard key={claim.id} claim={claim} />)}</div>}
      </div>
      <footer data-beat-part data-beat-conclusion className="mt-7 flex flex-wrap items-center justify-between gap-5 rounded-3xl bg-atc-navy px-6 py-5 text-white">
        <div className="max-w-3xl"><p className="text-xs font-bold tracking-[.16em] text-white/65">この数字が意味すること</p><p className="mt-2 font-bold leading-relaxed">{slide.conclusion}</p></div>
        <button type="button" onClick={() => onHandoff(slide.dashboardTab)} className="rounded-full bg-white px-5 py-3 text-sm font-bold text-atc-navy transition hover:bg-atc-surface focus-visible:outline-white">Dashboardで確かめる</button>
      </footer>
    </div>
  </article>;
}

export function DataStoryInterludes({ aggregates, onHandoff }: {
  aggregates: AggregatesArtifact;
  onHandoff(tab: DashboardHandoff['tab']): void;
}) {
  return <section data-data-stories aria-label="レポートから追加で見えた3つの論点">
    <header className="px-6 pb-4 pt-28 text-center md:px-12">
      <p className="text-xs font-bold tracking-[.22em] text-atc-primary">BEYOND THE SIX CHAPTERS</p>
      <h2 className="mt-4 text-3xl font-bold tracking-[-.04em] text-atc-navy md:text-5xl">数字をもう一段、深く読む。</h2>
      <p className="mx-auto mt-5 max-w-2xl text-sm font-medium leading-loose text-atc-slate">報告書とブランド資料を重ねた、追加の3つのデータストーリーです。推測と測定値を混同せず、読み方の限界も同時に示します。</p>
    </header>
    {[0, 1, 2].map((index) => <DataStorySlide key={index} aggregates={aggregates} index={index} onHandoff={onHandoff} />)}
  </section>;
}
